import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DeviceCreate } from '../../api/models';
import { clientService } from '../../services/clientService';
import { typesService } from '../../services/typesService';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { DeviceDetailModal } from './DeviceDetailModal';
import MultiSelectFilter, { MultiSelectOption } from '../common/MultiSelectFilter';
import config from '../../config/env';
import { safeStorage } from '../../utils/safeStorage';
import { isAbortError } from '../../utils/abortError';

interface PaginationInfo {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface DevicesTableProps {
  onDeviceSelect?: (device: DeviceCreate) => void;
  refreshTrigger?: number;
  initialDeviceTypeFilter?: string;
}

export const DevicesTable: React.FC<DevicesTableProps> = ({ onDeviceSelect, refreshTrigger, initialDeviceTypeFilter }) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { formatDate, t } = useI18n();
  const [devices, setDevices] = useState<DeviceCreate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // `debouncedSearch` is what actually hits the backend — 250 ms after
  // the user stops typing. Prevents a backend query on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<keyof DeviceCreate>('idDevice');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDevice, setSelectedDevice] = useState<DeviceCreate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  // Filters are multi-select. The string[] shape doubles as both the
  // popover's current selection AND the URL/backend payload — joined
  // with "," before being passed to /v2/conf (the backend parses the
  // comma list back into an IN clause, see provisioning.py).
  const [filterDeviceType, setFilterDeviceType] = useState<string[]>(initialDeviceTypeFilter ? [initialDeviceTypeFilter] : []);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterClient, setFilterClient] = useState<string[]>([]);
  const [filterTemplate, setFilterTemplate] = useState<string[]>([]);
  // Soft-delete: admins can opt in to see deactivated devices. Default off.
  const [showInactive, setShowInactive] = useState(false);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());
  const [clientList, setClientList] = useState<Array<{id: string, name: string}>>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [serverSidePagination, setServerSidePagination] = useState(true);

  // Debounce the search box so we only hit the backend once the user
  // pauses. 250 ms is long enough to batch typing bursts but short
  // enough to feel responsive.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Fetch devices with server-side pagination, filtering, and sorting.
  // All filter/sort state is pushed to the backend so the returned page
  // already reflects exactly what the user sees. No client-side filter.
  const fetchDevices = useCallback(async (page: number, pageSize: number, signal?: AbortSignal) => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build server-side filter/sort/search query string.
      const token = safeStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      params.set('full_data', 'true');
      // `slim=true` drops jsonAdditionalConfig, config_override_json,
      // and the other 9 columns the Devices table doesn't render.
      // At production scale this cuts the response payload ~20×.
      params.set('slim', 'true');
      if (filterClient.length) params.set('client_id', filterClient.join(','));
      if (filterDeviceType.length) params.set('device_type', filterDeviceType.join(','));
      if (filterTemplate.length) params.set('template_id', filterTemplate.join(','));
      if (filterStatus === 'refresh') params.set('needs_refresh', 'true');
      else if (filterStatus === 'active') params.set('needs_refresh', 'false');
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      if (showInactive && canSeeInactive) params.set('include_inactive', 'true');
      // Whitelist of sort columns on the backend. Sorting by client
      // name / mqttHost is NOT supported server-side since those aren't
      // indexed — fall back to idDevice.
      const SERVER_SORT_WHITELIST = new Set([
        'idDevice', 'dateTime', 'lastUpdateDateTime', 'deviceType', 'idClient', 'needsRefresh',
      ]);
      params.set(
        'sort_by',
        SERVER_SORT_WHITELIST.has(sortField as string) ? (sortField as string) : 'idDevice',
      );
      params.set('sort_dir', sortDirection);

      const url = `${config.api.baseUrl}/v2/conf?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch devices: ${response.statusText}`);
      }

      const data = await response.json();

      if (data && data.items && data.pagination) {
        if (config.dev.debug) {
          console.log('[DEVICES DEBUG] Received paginated response with', data.items.length, 'devices');
        }

        const mappedDevices = data.items.map((device: any) => ({
          ...device,
          mqttHost: device.mqtt_host,
          mqttPort: device.mqtt_port,
          mqttUser: device.mqtt_user,
          mqttPass: device.mqtt_pass,
          syslogHost: device.syslog_host,
          syslogPort: device.syslog_port,
          syslogFacility: device.syslog_facility,
          keepaliveTimer: device.keepalive_timer,
          dateTime: device.dateTime || device.date_time
        }));

        setDevices(mappedDevices);
        setPagination(data.pagination);
      } else if (Array.isArray(data)) {
        // Fallback: API returned simple array (backward compatible mode)
        if (config.dev.debug) {
          console.log('[DEVICES DEBUG] Received simple array with', data.length, 'items');
        }
        setDevices(data);
        setPagination(null);
        setServerSidePagination(false);
      } else {
        if (config.dev.debug) {
          console.warn('[DEVICES DEBUG] Unexpected response format:', data);
        }
        setDevices([]);
      }
    } catch (err: any) {
      if (isAbortError(err)) return;
      setError(err.message || 'Failed to fetch devices');
      console.error('Error fetching devices:', err);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [
    isAuthenticated,
    filterClient,
    filterDeviceType,
    filterTemplate,
    filterStatus,
    debouncedSearch,
    sortField,
    sortDirection,
    showInactive,
    canSeeInactive,
  ]);

  // Fetch devices when page, itemsPerPage, or auth changes. The
  // fetchDevices callback already closes over the current filter/sort
  // state, so changing any filter will also rebuild the callback and
  // trigger a refetch through this effect.
  useEffect(() => {
    if (!isAuthenticated) return;
    const ac = new AbortController();
    fetchDevices(currentPage, itemsPerPage, ac.signal);
    return () => ac.abort();
  }, [currentPage, itemsPerPage, isAuthenticated, refreshTrigger, fetchDevices]);

  // Any filter/search/sort change should bring us back to page 1 so
  // users don't end up on an empty page of a now-shorter result set.
  useEffect(() => {
    setCurrentPage(1);
  }, [filterClient, filterDeviceType, filterTemplate, filterStatus, debouncedSearch, sortField, sortDirection]);

  // Update filter when initialDeviceTypeFilter prop changes
  useEffect(() => {
    if (initialDeviceTypeFilter) {
      setFilterDeviceType([initialDeviceTypeFilter]);
    }
  }, [initialDeviceTypeFilter]);

  // Fetch all clients to get client names. clientService.fetchAllClients
  // is process-cached and dedups in-flight requests, so we don't pass a
  // signal there (cancelling would also cancel for any other consumer).
  // Instead, drop the result on the floor if the effect has already
  // cleaned up by the time the response arrives.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const clients = await clientService.fetchAllClients();
        if (cancelled) return;
        const namesMap = new Map<string, string>();
        const list: Array<{id: string, name: string}> = [];
        clients.forEach(client => {
          namesMap.set(client.id, client.name);
          list.push({ id: client.id, name: client.name });
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        setClientNames(namesMap);
        setClientList(list);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch client names:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, refreshTrigger]);

  // Filters / sort / search are all applied server-side (see fetchDevices).
  // This passthrough exists so the rest of the component can keep using
  // the same name, and so devices with missing essential identifiers
  // are still skipped defensively.
  const filteredAndSortedDevices = useMemo(() => {
    return devices.filter((device: any) => {
      const deviceId = device.idDevice || device.id_device || device.id;
      if (!device || !deviceId) {
        console.warn('Skipping device with missing deviceId:', device);
        return false;
      }
      return true;
    });
  }, [devices]);

  // Pagination - use server-side pagination when available, otherwise client-side
  const totalPages = serverSidePagination && pagination
    ? pagination.total_pages
    : Math.ceil(filteredAndSortedDevices.length / itemsPerPage);

  const totalItems = serverSidePagination && pagination
    ? pagination.total_items
    : filteredAndSortedDevices.length;

  const paginatedDevices = useMemo(() => {
    if (serverSidePagination && pagination) {
      // Server-side pagination: devices are already paginated
      return filteredAndSortedDevices;
    }
    // Client-side pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedDevices.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedDevices, currentPage, itemsPerPage, serverSidePagination, pagination]);

  const handleSort = (field: keyof DeviceCreate) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const handleRowClick = (device: DeviceCreate) => {
    setSelectedDevice(device);
    setIsModalOpen(true);
    if (onDeviceSelect) {
      onDeviceSelect(device);
    }
  };

  const handleDeviceUpdated = (updatedDevice: DeviceCreate) => {
    // Update the devices list
    setDevices(prevDevices =>
      prevDevices.map(device =>
        device.idDevice === updatedDevice.idDevice ? updatedDevice : device
      )
    );
    // Also update the selected device so the modal shows updated data
    setSelectedDevice(updatedDevice);
  };

  const getSortIcon = (field: keyof DeviceCreate) => {
    if (field !== sortField) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="devices-table-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading devices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="devices-table-error">
        <div className="error-container">
          <h3>{t('devices.errorLoading')}</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>{t('errors.retryAction')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="devices-table-container">
      <div className="devices-table-header">
        <div className="table-controls">
          <div className="search-container">
            <input
              type="text"
              data-testid="field-devices-search"
              placeholder={t('devices.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleFilterChange();
              }}
              className="search-input search-input--ota-style"
              aria-label={t('devices.searchPlaceholder')}
            />
          </div>
          <div className="filter-controls">
            <MultiSelectFilter
              value={filterClient}
              onChange={(next) => {
                setFilterClient(next);
                handleFilterChange();
              }}
              fetchOptions={async (search, includeInactiveOpt) => {
                const list = includeInactiveOpt
                  ? await clientService.fetchAllClients()
                  : await clientService.fetchActiveClients();
                const needle = search.toLowerCase();
                return list
                  .filter((c) => !needle || c.name.toLowerCase().includes(needle))
                  .slice(0, 50)
                  .map((c) => ({ id: c.id, name: c.name }));
              }}
              fetchSelectedLabels={async (ids) => {
                const out: Record<string, string> = {};
                for (const id of ids) {
                  const found = clientList.find((c) => c.id === id);
                  if (found) out[id] = found.name;
                }
                return out;
              }}
              allLabel={t('devices.allClients')}
              ariaLabel="Filter by client"
              className="filter-client"
              testId="filter-client"
              searchPlaceholder={t('devices.allClients')}
              includeInactive={showInactive && canSeeInactive}
            />
            <MultiSelectFilter
              value={filterDeviceType}
              onChange={(next) => {
                setFilterDeviceType(next);
                handleFilterChange();
              }}
              fetchOptions={async (search, includeInactiveOpt) => {
                const types = await typesService.getHardwareTypes(includeInactiveOpt);
                const needle = search.toLowerCase();
                return types
                  .filter((t) => !needle || t.code.toLowerCase().includes(needle) || (t.name || '').toLowerCase().includes(needle))
                  .slice(0, 50)
                  .map((tt) => ({ id: tt.code, name: tt.name ? `${tt.name} (${tt.code})` : tt.code }));
              }}
              fetchSelectedLabels={async (codes) => {
                const out: Record<string, string> = {};
                try {
                  const types = await typesService.getHardwareTypes(showInactive && canSeeInactive);
                  for (const code of codes) {
                    const found = types.find((tt) => tt.code === code);
                    out[code] = found ? (found.name ? `${found.name} (${found.code})` : found.code) : code;
                  }
                } catch {
                  /* fall back to id */
                }
                return out;
              }}
              allLabel={'All Device Types'}
              ariaLabel="Filter by type"
              testId="filter-device-type"
              searchPlaceholder={'All Device Types'}
              includeInactive={showInactive && canSeeInactive}
            />
            <MultiSelectFilter
              value={filterTemplate}
              onChange={(next) => {
                setFilterTemplate(next);
                handleFilterChange();
              }}
              fetchOptions={async (search, includeInactiveOpt) => {
                const token = safeStorage.getItem('access_token');
                const params = new URLSearchParams();
                params.set('page', '1');
                params.set('limit', '50');
                if (search.trim()) params.set('search', search.trim());
                if (includeInactiveOpt) params.set('include_inactive', 'true');
                const resp = await fetch(`${config.api.baseUrl}/v2/config-templates?${params.toString()}`, {
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                if (!resp.ok) return [];
                const data = await resp.json();
                return (data?.items || [])
                  .map((tmpl: any) => ({ id: String(tmpl.id), name: String(tmpl.name) }))
                  .filter((o: MultiSelectOption) => o.id && o.name);
              }}
              fetchSelectedLabels={async (ids) => {
                const out: Record<string, string> = {};
                const token = safeStorage.getItem('access_token');
                await Promise.all(
                  ids.filter((i) => i !== '0').map(async (id) => {
                    try {
                      const r = await fetch(`${config.api.baseUrl}/v2/config-templates/${id}`, {
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      });
                      if (r.ok) {
                        const tmpl = await r.json();
                        if (tmpl?.name) out[id] = String(tmpl.name);
                      }
                    } catch {
                      /* leave id as label */
                    }
                  }),
                );
                return out;
              }}
              allLabel={t('configTemplates.allTemplates')}
              ariaLabel="Filter by template"
              extraOptions={[{ id: '0', label: t('devices.noTemplate') }]}
              className="filter-template"
              testId="filter-template"
              searchPlaceholder={t('configTemplates.searchPlaceholder')}
              includeInactive={showInactive && canSeeInactive}
            />

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                handleFilterChange();
              }}
              className="filter-select"
            >
              <option value="">{t('common.allStatus')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="refresh">{t('devices.needsRefresh')}</option>
            </select>
            {canSeeInactive && (
              <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    handleFilterChange();
                  }}
                />
                {t('common.showInactive')}
              </label>
            )}
          </div>
          <div className="table-info">
            {/* totalItems is the full match count across pages (server-side
                pagination) — the current page length would under-report. */}
            {t('devices.devicesFound', { count: totalItems })}
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="devices-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('idDevice')} className="sortable">
                {t('devices.deviceId')} {getSortIcon('idDevice')}
              </th>
              <th onClick={() => handleSort('deviceType')} className="sortable">
                {t('devices.deviceType')} {getSortIcon('deviceType')}
              </th>
              <th className="sortable">
                {t('common.model')}
              </th>
              <th onClick={() => handleSort('dateTime')} className="sortable">
                {t('devices.lastUpdated')} {getSortIcon('dateTime')}
              </th>
              <th>{t('devices.client')}</th>
              <th onClick={() => handleSort('needsRefresh')} className="sortable">
                {t('devices.status')} {getSortIcon('needsRefresh')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedDevices.map((device: any) => (
              <tr
                key={device.idDevice}
                onClick={() => handleRowClick(device)}
                className="device-row"
              >
                <td className="device-id">{device.idDevice}</td>
                <td className="device-type">{device.deviceType || 'N/A'}</td>
                <td className="device-model">{device.model || device.model_ref || '-'}</td>
                <td className="date-time">
                  {formatDate(device.dateTime)}
                </td>
                <td className="device-client">
                  {clientNames.get(device.idClient) || clientNames.get(device.idClient?.replace(/-/g, '')) || '-'}
                </td>
                <td className="status">
                  <span className={`status-badge ${device.needsRefresh ? 'needs-refresh' : 'active'}`}>
                    {device.needsRefresh ? t('devices.needsRefresh') : t('common.active')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {paginatedDevices.length === 0 && !loading && (
          <div className="no-devices">
            <p>No devices found matching your search criteria.</p>
          </div>
        )}
      </div>

      <div className="pagination-wrapper">
        <div className="pagination-info">
          <span>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} devices
          </span>
          <div className="items-per-page">
            <label htmlFor="itemsPerPage">Items per page:</label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="items-per-page-select"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              First
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>

            <div className="page-numbers">
              {/* Show limited page numbers for better UX with large datasets */}
              {(() => {
                const pages = [];
                const maxVisiblePages = 5;
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                if (endPage - startPage < maxVisiblePages - 1) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      className={`page-btn ${i === currentPage ? 'active' : ''}`}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Last
            </button>
          </div>
        )}
      </div>

      <DeviceDetailModal
        device={selectedDevice}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDevice(null);
        }}
        onDeviceUpdated={handleDeviceUpdated}
      />
    </div>
  );
};